import { Address, BillingAddress, CartVariant, NumericIdentifiable } from '@mkp/shared/models';
import { User } from '@mkp/user/models';

export interface ProductPrice {
  unit: number;
  originalUnit: number;
  total: number;
  subtotal: number;
  tax: number;
  discount: number;
  creditNoteDiscounts: number[];
}

/**
 * Represents the state of a coupon during checkout operations.
 */
export type CouponState = 'empty' | 'applying' | 'applied' | 'failure' | 'removing';

/**
 * CartItemDto - Updated to match e-commerce API response structure
 */
export interface CartItemDto extends NumericIdentifiable {
  quantity: number;
  unitPrice: number;
  originalUnitPrice: number;
  total: number;
  discountedUnitPrice: number;
  subtotal: number;
  productName: string;
  variant: CartVariant;
}

/**
 * CartDto - Updated to match e-commerce API response structure
 * This interface now directly reflects the API response from the cart creation endpoint.
 */
export interface CartDto {
  tokenValue: string;
  number: string | null;
  channel: {
    code: string;
    name: string;
  };
  currencyCode: string;
  localeCode: string;
  customer: {
    email: string;
  };
  billingAddress: BillingAddress | null;
  promotionCoupon: { code: string } | null;
  items: CartItemDto[];
  payments: Array<{
    id: number;
  }>;
  // Flat totals structure (no longer nested)
  total: number;
  itemsTotal: number;
  itemsSubtotal: number;
  taxTotal: number;
  taxExcludedTotal: number;
  taxIncludedTotal: number;
  orderPromotionTotal: number;
}

export function toBillingAddress(
  selectedAccount: { legalEntity: { name: string; address: Address } },
  // Structural rather than User: callers pass both User and UserDto, which are duplicate models.
  billingUser: Pick<User, 'firstName' | 'lastName' | 'phoneNumber'>
): BillingAddress {
  const { address, name: legalEntityName } = selectedAccount.legalEntity;

  return {
    city: address.city,
    company: legalEntityName,
    postcode: address.postalCode,
    countryCode: address.countryIsoCode,
    street: address.street as unknown as string,
    firstName: billingUser.firstName,
    lastName: billingUser.lastName,
    phoneNumber: billingUser.phoneNumber as unknown as string,
  };
}
